import React, { useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';
import './ThemeToggle.css';

const darkQuery = () => window.matchMedia('(prefers-color-scheme: dark)');

/**
 * Appearance switch. Defaults to the system preference and is deliberately not
 * persisted — the override lives only as a data-theme attribute on <html>, so a
 * reload drops back to system. State is seeded from that attribute so the icon
 * stays correct when the footer remounts on a route change.
 */
const ThemeToggle = () => {
  const [override, setOverride] = useState(() => document.documentElement.dataset.theme || null);
  const [systemDark, setSystemDark] = useState(() => darkQuery().matches);

  // Follow the system live while no override is set.
  useEffect(() => {
    const mq = darkQuery();
    const onChange = (event) => setSystemDark(event.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const isDark = override ? override === 'dark' : systemDark;

  const toggle = () => {
    const next = isDark ? 'light' : 'dark';

    if ((next === 'dark') === systemDark) {
      // Back on the system preference — drop the override so it tracks again.
      delete document.documentElement.dataset.theme;
      setOverride(null);
    } else {
      document.documentElement.dataset.theme = next;
      setOverride(next);
    }
  };

  const label = isDark ? 'Switch to light appearance' : 'Switch to dark appearance';

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={toggle}
      aria-label={label}
      title={label}
    >
      {isDark
        ? <Sun size={17} strokeWidth={1.75} aria-hidden="true" />
        : <Moon size={17} strokeWidth={1.75} aria-hidden="true" />}
    </button>
  );
};

export default ThemeToggle;
